import { useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { HeaderNav } from "../components/HeaderNav";
import { authApi, errDetail } from "../lib/api";
import { startGoogleLogin, useAuth } from "../lib/auth";
import { useLang } from "../lib/i18n";

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" /><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" /><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z" /><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" /></svg>
);

export default function Login() {
  const { t, te } = useLang();
  const { user, setUser } = useAuth();
  const [params] = useSearchParams();
  const next = params.get("next") || "/studio";
  const nav = useNavigate();
  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (user) return <Navigate to={next} replace />;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup" && form.password.length < 8) { setError(te("Le mot de passe doit contenir au moins 8 caractères")); return; }
    setBusy(true);
    try {
      const u = mode === "signup" ? await authApi.register(form.email, form.password, form.name) : await authApi.login(form.email, form.password);
      setUser(u);
      nav(next, { replace: true });
    } catch (err) { setError(te(errDetail(err))); } finally { setBusy(false); }
  };

  return (
    <div className="frz-page">
      <HeaderNav />
      <main className="frz-auth" data-testid="auth-page">
        <div className="frz-auth-card">
          <img src="/brand/logo-256.jpg" alt="" className="frz-auth-logo" />
          <h1 className="frz-h2">{mode === "signup" ? t("auth_signup_title") : t("auth_login_title")}</h1>
          <p className="frz-muted">{mode === "signup" ? t("auth_signup_sub") : t("auth_login_sub")}</p>
          <button type="button" className="frz-btn frz-btn-google frz-btn-block" onClick={() => startGoogleLogin(next)} data-testid="google-login-button">
            <GoogleIcon /> {t("auth_google")}
          </button>
          <div className="frz-or"><span>{t("auth_or")}</span></div>
          <form onSubmit={submit} className="frz-form" data-testid={`${mode}-form`}>
            {mode === "signup" && (
              <label className="frz-field"><span>{t("auth_name")}</span>
                <input value={form.name} onChange={set("name")} autoComplete="name" data-testid="auth-name-input" /></label>
            )}
            <label className="frz-field"><span>{t("auth_email")}</span>
              <input type="email" required value={form.email} onChange={set("email")} autoComplete="email" data-testid="auth-email-input" /></label>
            <label className="frz-field"><span>{t("auth_password")}</span>
              <input type="password" required value={form.password} onChange={set("password")}
                autoComplete={mode === "signup" ? "new-password" : "current-password"} data-testid="auth-password-input" /></label>
            {error && <p className="frz-error" data-testid="auth-error">{error}</p>}
            <button type="submit" className="frz-btn frz-btn-primary frz-btn-block" disabled={busy} data-testid="auth-submit-button">
              {busy && <Loader2 size={16} className="spin" />} {mode === "signup" ? t("auth_signup_btn") : t("auth_login_btn")}
            </button>
          </form>
          {mode === "login" && <Link to="/forgot-password" className="frz-link frz-small" data-testid="forgot-password-link">{t("auth_forgot")}</Link>}
          <p className="frz-small frz-muted">
            {mode === "signup" ? t("auth_have_account") : t("auth_no_account")}{" "}
            <button type="button" className="frz-linkbtn" onClick={() => { setMode(mode === "signup" ? "login" : "signup"); setError(""); }} data-testid="auth-switch-mode">
              {mode === "signup" ? t("auth_login_btn") : t("auth_signup_btn")}
            </button>
          </p>
        </div>
      </main>
    </div>
  );
}
